// Access to a single company's server.
//
// Every call goes to the URL from the directory entry and is signed with the
// device key assigned to that company. Sessions are independent — a token issued
// by one is worthless with any other.

import type { PublicKeyInfo } from '$lib/crypto/device-keys';

import { getBytes, joinPath, send } from './http';
import { parseCompanyView, parseEvent, parseEventPage, parseSessionResponse, parseViewList } from './types';
import type { CompanyView, EventPage, ImpEvent, ViewDescriptor } from './types';

export interface SessionResult {
  token: string;
  expiresAtMs: number;
  threadId: string;
  accountId: string;
  /** The trading name, as this server reports it — the directory does not carry
   *  names, so this is where the conversation list gets something other than a
   *  phone number to show. Security never rested on it. */
  distributorName: string | undefined;
  /** A freshly registered device can talk straight away, but during the waiting
   *  period it gets neither the archive nor irreversible actions. What is gated
   *  is value, not contact. */
  quarantineUntilMs: number | undefined;
}

function toSessionResult(body: Record<string, unknown>): SessionResult {
  const parsed = parseSessionResponse(body);
  const quarantine = parsed.quarantine;
  return {
    token: parsed.token,
    expiresAtMs: parsed.expiresAt?.getTime() ?? Date.now() + 60 * 60 * 1000,
    threadId: parsed.threadId ?? '',
    accountId: parsed.accountId ?? '',
    distributorName: parsed.distributorName,
    quarantineUntilMs:
      quarantine !== undefined && quarantine.active ? quarantine.until?.getTime() : undefined,
  };
}

/** Device key registration. It has to precede asking for an SMS code: registration
 *  costs nothing, so it can be rate-limited as aggressively as you like, and the
 *  only step that costs money stays behind a free gate. */
export async function registerDevice(input: {
  serverUrl: string;
  key: PublicKeyInfo;
  model?: string;
}): Promise<string> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/devices'),
    body: {
      public_key: { alg: input.key.alg, key: input.key.key, id: input.key.id },
      platform: 'web',
      ...(input.model !== undefined ? { model: input.model } : {}),
    },
  });
  const deviceId = result.body['device_id'];
  return typeof deviceId === 'string' ? deviceId : '';
}

export async function startVerification(input: {
  serverUrl: string;
  accountId: string;
  phone: string;
  deviceId: string;
}): Promise<void> {
  // No `app_hash`: that field exists for Android's SMS Retriever. On the web, iOS
  // autofills from the keyboard and everyone else types six digits.
  await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/verification/start'),
    signingAccountId: input.accountId,
    body: { number: input.phone, device_id: input.deviceId },
  });
}

export async function completeVerification(input: {
  serverUrl: string;
  accountId: string;
  phone: string;
  deviceId: string;
  code: string;
}): Promise<SessionResult> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/verification/complete'),
    signingAccountId: input.accountId,
    body: { number: input.phone, device_id: input.deviceId, code: input.code.trim() },
  });
  return toSessionResult(result.body);
}

export async function refreshSession(input: {
  serverUrl: string;
  accountId: string;
  token: string;
}): Promise<SessionResult> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/sessions/refresh'),
    signingAccountId: input.accountId,
    token: input.token,
  });
  return toSessionResult(result.body);
}

/** How the client catches up — on open, on the foreground timer, and after a
 *  wake nudged it. The wake never carries the events; this fetch does. */
export async function getEvents(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  cursor?: string;
  limit?: number;
  /** Seconds the server may hold the request open. */
  waitS?: number;
  signal?: AbortSignal;
}): Promise<EventPage> {
  const limit = input.limit ?? 50;
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, '/v1/events', {
      ...(input.cursor !== undefined ? { cursor: input.cursor } : {}),
      limit: `${limit}`,
      ...(input.waitS !== undefined ? { wait: `${input.waitS}` } : {}),
    }),
    signingAccountId: input.accountId,
    token: input.token,
    // The server may sit on the request for the whole wait; our own limit has to
    // outlast it, or we would abandon a request that was about to answer.
    timeoutMs: input.waitS === undefined ? 20_000 : (input.waitS + 10) * 1000,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  });
  return parseEventPage(result.body);
}

/** Idempotent on `uuid`: a repeat after a dropped connection returns the same
 *  event instead of creating a second order. */
export async function sendMessage(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  uuid: string;
  threadId: string;
  elements: Record<string, unknown>[];
}): Promise<ImpEvent> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/messages'),
    signingAccountId: input.accountId,
    token: input.token,
    body: { uuid: input.uuid, thread_id: input.threadId, elements: input.elements },
  });
  return parseEvent(result.body);
}

export async function uploadMedia(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  bytes: Uint8Array;
  contentType: string;
}): Promise<string> {
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/media'),
    signingAccountId: input.accountId,
    token: input.token,
    bytes: input.bytes,
    contentType: input.contentType,
    timeoutMs: 120_000,
  });
  const mediaId = result.body['media_id'];
  return typeof mediaId === 'string' ? mediaId : '';
}

export async function downloadMedia(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  mediaId: string;
}): Promise<Uint8Array> {
  return getBytes({
    url: joinPath(input.serverUrl, `/v1/media/${encodeURIComponent(input.mediaId)}`),
    signingAccountId: input.accountId,
    token: input.token,
  });
}

/** The client sends quantities and a decision only. It neither sends nor
 *  recomputes prices. */
export async function respondToCart(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  uuid: string;
  cartId: string;
  decision: string;
  items?: { itemId: string; quantity: number }[];
  note?: string;
}): Promise<ImpEvent> {
  const items = input.items ?? [];
  const result = await send({
    method: 'POST',
    url: joinPath(input.serverUrl, '/v1/cart/respond'),
    signingAccountId: input.accountId,
    token: input.token,
    body: {
      uuid: input.uuid,
      cart_id: input.cartId,
      decision: input.decision,
      ...(items.length > 0
        ? { items: items.map((p) => ({ item_id: p.itemId, quantity: p.quantity })) }
        : {}),
      ...(input.note !== undefined ? { note: input.note } : {}),
    },
  });
  return parseEvent(result.body);
}

/** The whole history in one bundle. Called on the user's request and automatically
 *  once the client sees `withdrawn` — while the server still responds. */
export async function exportHistory(input: {
  serverUrl: string;
  accountId: string;
  token: string;
}): Promise<Record<string, unknown>> {
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, '/v1/export'),
    signingAccountId: input.accountId,
    token: input.token,
    timeoutMs: 120_000,
  });
  return result.body;
}

/** Hands over (or renews) the wake capability, together with the per-relation
 *  notification key that lets this server attach an encrypted preview to its
 *  wakes, and the current wake-mute list. Renewed on every app open and after
 *  every mute change. */
export async function putCapability(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  capability: string;
  validUntil: string;
  gatewayId: string;
  notificationKey: string;
  mutedCategories: string[];
}): Promise<void> {
  await send({
    method: 'PUT',
    url: joinPath(input.serverUrl, '/v1/capabilities'),
    signingAccountId: input.accountId,
    token: input.token,
    body: {
      capability: input.capability,
      valid_until: input.validUntil,
      gateway_id: input.gatewayId,
      notification_key: input.notificationKey,
      muted_categories: input.mutedCategories,
    },
  });
}

/** The pages this company publishes for this buyer — descriptors only. Asked for often,
 *  which is why the bodies live behind a second call: an unchanged catalogue then costs
 *  one small request and no download. */
export async function listViews(input: {
  serverUrl: string;
  accountId: string;
  token: string;
}): Promise<ViewDescriptor[]> {
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, '/v1/views'),
    signingAccountId: input.accountId,
    token: input.token,
  });
  return parseViewList(result.body);
}

/** One page. A 404 means it is gone — the caller drops it from the cache and from the
 *  menu without telling anyone, because a page that was withdrawn is not news. */
export async function getView(input: {
  serverUrl: string;
  accountId: string;
  token: string;
  viewId: string;
}): Promise<CompanyView> {
  const result = await send({
    method: 'GET',
    url: joinPath(input.serverUrl, `/v1/views/${encodeURIComponent(input.viewId)}`),
    signingAccountId: input.accountId,
    token: input.token,
  });
  return parseCompanyView(result.body);
}

export async function deleteContactOnServer(input: {
  serverUrl: string;
  accountId: string;
  token: string;
}): Promise<void> {
  await send({
    method: 'DELETE',
    url: joinPath(input.serverUrl, '/v1/contact'),
    signingAccountId: input.accountId,
    token: input.token,
  });
}
